/**
 * createClient — framework-agnostic fetch wrapper used by Configural UIs.
 *
 * Why a custom client rather than axios? We don't need interceptors,
 * cancel tokens, or adapters — just a thin JSON layer with header
 * providers. Using `fetch` means zero extra bytes in the UI bundle and
 * lets us run the same code in tests (via undici/fetch) without mocking
 * an adapter.
 *
 *     const client = createClient({
 *       baseUrl: "/api",
 *       credentials: "include",
 *       headers: [orgHeader, subTenantHeader],
 *     });
 *     const { user } = await client.get("/auth/whoami");
 *     await client.post("/sessions", { body: { email, password } });
 *
 * Every method returns the parsed response body on 2xx; any non-2xx
 * response throws an `ApiError`. Query strings are built via URL so
 * the caller never has to escape anything manually — pass a `query`
 * object and the client handles it.
 */

import { ApiError } from "./error.js";
import { mergeHeaderProviders } from "./headers.js";

/**
 * @typedef {object} RequestOptions
 * @property {unknown}                      [body]    JSON-serializable body.
 * @property {Record<string, string|number|boolean|null|undefined>} [query]
 *                                                    Values that are null/undefined/empty string are dropped.
 * @property {Record<string, string>}       [headers] Per-request header overrides.
 * @property {AbortSignal}                  [signal]  Abort signal, forwarded to fetch.
 */

/**
 * @typedef {object} ClientOptions
 * @property {string} baseUrl     Prefix every request path with this. Typically "/api".
 * @property {"include" | "same-origin" | "omit"} [credentials="include"]
 *                                Credentials mode for fetch. Default "include" for
 *                                cookie-based auth; pass "omit" for bearer-only.
 * @property {Array<() => Record<string, string> | null | undefined>} [headers]
 *                                Header providers; called once per request.
 * @property {typeof fetch}       [fetchImpl] Override fetch (tests).
 */

/**
 * Build a URL for a request given a base, path, and query map.
 * Exported for test use.
 *
 * @param {string} baseUrl
 * @param {string} path
 * @param {Record<string, unknown>} [query]
 */
export function buildUrl(baseUrl, path, query) {
  // Use a dummy origin so URL parses relative paths; we serialize with
  // `.pathname + .search` at the end.
  const origin =
    typeof window !== "undefined" && window.location
      ? window.location.origin
      : "http://local.invalid";
  const u = new URL(baseUrl + path, origin);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === "") continue;
      u.searchParams.set(k, String(v));
    }
  }
  // If the caller passed an absolute base (http://…) keep it; otherwise
  // return a root-relative URL so the browser hits the same origin.
  if (/^https?:\/\//i.test(baseUrl)) return u.toString();
  return u.pathname + u.search;
}

/**
 * Create a new API client bound to a base URL + header providers.
 *
 * @param {ClientOptions} opts
 */
export function createClient({
  baseUrl,
  credentials = "include",
  headers: headerProviders = [],
  fetchImpl,
} = {}) {
  // `baseUrl` may be an empty string — that's how callers opt into
  // passing absolute-from-root paths ("/api/v1/...", "/tenants/...")
  // that don't share a common prefix. Only `undefined`/`null` are
  // invalid.
  if (baseUrl === undefined || baseUrl === null) {
    throw new Error("createClient: baseUrl is required (use '' for absolute paths)");
  }
  const doFetch = fetchImpl || fetch;

  /**
   * @param {string} method
   * @param {string} path
   * @param {RequestOptions} [opts]
   */
  async function request(method, path, { body, query, headers = {}, signal } = {}) {
    const url = buildUrl(baseUrl, path, query);

    const merged = {
      "Content-Type": "application/json",
      ...mergeHeaderProviders(headerProviders),
      ...headers,
    };

    const init = {
      method,
      headers: merged,
      credentials,
      signal,
    };
    if (body !== undefined) {
      init.body = typeof body === "string" ? body : JSON.stringify(body);
    }

    const res = await doFetch(url, init);
    const ct = res.headers.get("content-type") || "";
    const parsed = ct.includes("application/json")
      ? await res.json().catch(() => null)
      : await res.text();

    if (!res.ok) throw new ApiError(res.status, parsed, url);
    return parsed;
  }

  return {
    request,
    get: (path, opts) => request("GET", path, opts),
    post: (path, opts) => request("POST", path, opts),
    put: (path, opts) => request("PUT", path, opts),
    patch: (path, opts) => request("PATCH", path, opts),
    del: (path, opts) => request("DELETE", path, opts),
  };
}
